import { format, parseISO, subDays } from "date-fns";

/**
 * The day before a change takes effect, which is the last day the old
 * parent prices the account.
 *
 * Kept in local time throughout. Stepping back a day and then reading the
 * date off toISOString would cross midnight back into the previous day
 * everywhere east of Greenwich.
 */
export const dayBefore = (isoDate: string): string => {
  const date = parseISO(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }
  return format(subDays(date, 1), "yyyy-MM-dd");
};

/**
 * What the change does to pricing, said plainly enough to be read before
 * saving. Prices cascade from the parent, so the date decides which parent
 * prices which days, and an account with no parent prices itself.
 *
 * Adding a parent to an account that already has one is how a parent gets
 * changed, and the two read differently: one account is pricing itself
 * until the date, the other is still being priced by the parent it is
 * leaving. Which sentence applies is decided by whether there is a current
 * parent.
 */
export const effectText = ({
  currentParentNo,
  newParentNo,
  validFrom,
}: {
  currentParentNo: string | undefined;
  newParentNo: string;
  validFrom: string;
}): string => {
  const parent = newParentNo ? `#${newParentNo}` : "the new parent";

  if (currentParentNo) {
    return (
      `Until ${dayBefore(validFrom)} prices still cascade from ` +
      `#${currentParentNo}. From ${validFrom} they come from ${parent}. ` +
      `Exceptions on this account are kept.`
    );
  }

  return (
    `Until ${dayBefore(validFrom)} this account keeps pricing itself. ` +
    `From ${validFrom} prices cascade from ${parent}. Exceptions on this ` +
    `account are kept.`
  );
};
